import { FoldersOrder } from "@zapier/bff";
import {
  DEFAULT_FOLDER_ID,
  FOLDER_TRASH_ID,
  FOLDERS_PAGE_SIZE,
  FolderTypeQueryState,
} from "@zapier/utils";
import { useMemo } from "react";

import { useMembers } from "../member/useMembers";
import { useUserAccountRole } from "../permissions/useUserAccountRole";
import { useSplitTreatments } from "../split/useSplitTreatments";
import { trpc } from "../trpc";
import { useCurrentUser } from "../user/useCurrentUser";
import useFoldersFiltering from "./useFoldersFiltering";
import { useQueryFolderTree } from "./useQueryFolderTree";
import useVirtualFolders from "./useVirtualFolders";
import { getOwnerIdsFromTree } from "./utils/getOwnerIdsFromTree";
import { mapOwnersToFolders } from "./utils/mapOwnersToFolders";
import { mapOwnersToFoldersHash } from "./utils/mapOwnerToFoldersHash";

interface Input {
  disableOrdering?: boolean;
  folderType?: FolderTypeQueryState | null;
  includeDefaultFolder?: boolean;
  includeTrashFolder?: boolean;
  order?: FoldersOrder;
  page?: number | null;
  pageSize?: number;
  searchQuery?: string;
  selectedOwner?: number | null;
}

interface Options {
  enabled?: boolean;
}

export function useFolderTree(
  {
    disableOrdering = false,
    folderType,
    includeDefaultFolder,
    includeTrashFolder,
    order,
    page = 0,
    pageSize = FOLDERS_PAGE_SIZE,
    searchQuery = "",
    selectedOwner,
  }: Input,
  { enabled = true }: Options = {},
) {
  // Fetch folder tree
  const {
    folders,
    hash,
    isFoldersLoading: isFolderTreeLoading,
  } = useQueryFolderTree({ enabled });

  const { filterFolders } = useFoldersFiltering();
  const { trashFolder, defaultPersonalFolder } = useVirtualFolders();
  const { user } = useCurrentUser();
  const { isSuperAdminPersonalFoldersEnabled } = useSplitTreatments();
  const { isAccountOwner, isSuperAdmin } = useUserAccountRole();

  const { data: personalFolderAssets } =
    trpc.asset.getHydratedFolderAssets.useQuery(
      { folderId: DEFAULT_FOLDER_ID, limit: 1, offset: 0 },
      { enabled: !!includeDefaultFolder },
    );

  // Someone else's Personal folder, looked up via the existing Owner filter.
  // Reuses the same single-owner endpoint above, scoped to whoever's selected.
  // Gated by role (matches the sidebar accordion's gate) and by
  // assetmanagement_super_admin_personal_folders so it can be killed
  // instantly without a redeploy.
  const isSelectedOwnerSomeoneElse =
    isSuperAdminPersonalFoldersEnabled &&
    (isAccountOwner || isSuperAdmin) &&
    !!selectedOwner &&
    selectedOwner !== user?.id;

  const {
    data: selectedOwnerFolderAssets,
    isLoading: isSelectedOwnerAssetsLoading,
  } = trpc.asset.getHydratedFolderAssets.useQuery(
    {
      folderId: DEFAULT_FOLDER_ID,
      limit: 1,
      offset: 0,
      ownerId: selectedOwner ?? undefined,
    },
    { enabled: !!includeDefaultFolder && isSelectedOwnerSomeoneElse },
  );

  // Get unique ownerIds recursively from the folders tree
  const ownerIds = useMemo(() => {
    const treeOwnerIds = getOwnerIdsFromTree(folders);
    const currentUserId = user?.id;
    const ids = new Set(treeOwnerIds);

    // Always include current user ID to ensure personal folder owner is fetched
    if (currentUserId) {
      ids.add(currentUserId);
    }
    // Also fetch the selected owner so we can name their Personal folder below
    if (isSelectedOwnerSomeoneElse) {
      ids.add(selectedOwner as number);
    }

    return [...ids];
  }, [folders, user?.id, selectedOwner, isSelectedOwnerSomeoneElse]);

  // Fetch all members referenced in the folders tree
  const membersQuery = useMembers({
    ids: ownerIds || [],
  });

  // The selected owner's Personal folder depends on two extra requests beyond
  // the main tree (their unfiled count + their name); fold those into the
  // overall loading state so the row appears with everything else instead of
  // popping in late once the main list has already rendered.
  const isFoldersLoading =
    isFolderTreeLoading ||
    (isSelectedOwnerSomeoneElse &&
      (isSelectedOwnerAssetsLoading || membersQuery.isLoading));

  const selectedOwnerUnfiledCount = selectedOwnerFolderAssets?.meta?.count;
  const selectedOwnerMember = isSelectedOwnerSomeoneElse
    ? membersQuery.members?.[selectedOwner as number]
    : undefined;

  // Someone else's Personal folder shows up once we know their name (so it
  // renders correctly) — same as the viewer's own Personal folder, it's
  // always shown regardless of count, including zero, so an admin filtering
  // by a member with no unfiled zaps sees "Personal (0)" rather than a
  // confusing blanket "no results".
  const selectedOwnerPersonalFolder = useMemo(() => {
    if (!selectedOwnerMember) {
      return undefined;
    }

    return {
      accountId: defaultPersonalFolder.accountId,
      id: DEFAULT_FOLDER_ID,
      isShared: false,
      name: `${selectedOwnerMember.fullName} (Personal)`,
      ownerId: selectedOwner as number,
      assetCount: selectedOwnerUnfiledCount ?? 0,
      parentId: undefined,
      rootId: undefined,
    };
  }, [
    defaultPersonalFolder.accountId,
    selectedOwner,
    selectedOwnerMember,
    selectedOwnerUnfiledCount,
  ]);

  // Map owners to folders
  const hydratedFolders = useMemo(() => {
    const unHydratedFolders = [...folders];

    if (includeDefaultFolder) {
      unHydratedFolders.unshift({
        ...defaultPersonalFolder,
        assetCount: personalFolderAssets?.meta?.count,
      });
      if (selectedOwnerPersonalFolder) {
        unHydratedFolders.unshift(selectedOwnerPersonalFolder);
      }
    }
    if (includeTrashFolder) {
      // Add trash folder to the end of the folders array
      unHydratedFolders.push(trashFolder);
    }

    return mapOwnersToFolders(unHydratedFolders, membersQuery.members, {
      isMembersPending: membersQuery.isPending,
    });
  }, [
    folders,
    membersQuery.members,
    membersQuery.isPending,
    includeDefaultFolder,
    includeTrashFolder,
    trashFolder,
    defaultPersonalFolder,
    personalFolderAssets,
    selectedOwnerPersonalFolder,
  ]);

  // Map members to hash
  const hydratedHash = useMemo(() => {
    return mapOwnersToFoldersHash(hash, membersQuery.members);
  }, [hash, membersQuery.members]);

  const filteredFolders = useMemo(() => {
    // If there is a search query or folderType or owner filter, filter folders
    if (searchQuery || folderType || selectedOwner || order) {
      // Return back folders that match search query in foldersTree
      // Filter out virtual folders since they have no ownerId and no actions can be taken on them
      const _filteredFolders = filterFolders({
        disableOrdering,
        folders: hydratedFolders,
        folderType,
        order,
        searchQuery,
        selectedOwner,
      });

      // Filter out trash and default folder if they are not to be included
      // return _filteredFolders;
      return _filteredFolders.filter((folder) => {
        if (!includeTrashFolder && folder.id === FOLDER_TRASH_ID) {
          return false;
        }
        if (!includeDefaultFolder && folder.id === DEFAULT_FOLDER_ID) {
          return false;
        }
        return true;
      });
    }

    // If there is no search query, folderType, selected owner or order return all folders
    return hydratedFolders;
  }, [
    disableOrdering,
    filterFolders,
    folderType,
    hydratedFolders,
    includeDefaultFolder,
    includeTrashFolder,
    searchQuery,
    order,
    selectedOwner,
  ]);

  // Pagination done on frontend since useQueryFolderTree does not support pagination and returns entire tree
  const totalPages = filteredFolders?.length
    ? Math.ceil(filteredFolders?.length / pageSize)
    : 1;
  const totalResults = filteredFolders?.length || 0;

  const paginatedFolders = useMemo(
    () =>
      filteredFolders?.slice(
        (page ? page - 1 : 0) * pageSize,
        (page || 1) * pageSize,
      ),
    [page, filteredFolders, pageSize],
  );

  return {
    folders: filteredFolders,
    hash: hydratedHash,
    paginatedFolders,
    isFoldersLoading,
    totalPages,
    totalResults,
  };
}
